import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, FileText, Clock, CheckCircle2, Sparkles, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

const STATUS_LABEL = {
  nouvelle: { label: "Nouvelle", cls: "bg-blue-100 text-blue-800" },
  en_traitement: { label: "En traitement", cls: "bg-amber-100 text-amber-800" },
  assignee: { label: "Assignée", cls: "bg-purple-100 text-purple-800" },
  terminee: { label: "Terminée", cls: "bg-emerald-100 text-emerald-800" },
};

export default function ClientDashboard() {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  useEffect(() => { api.get("/my/requests").then((r) => setRequests(r.data)); }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="client-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-1">Tableau de bord</p>
          <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">Bonjour {user?.name}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to="/contact?prefill=role_change"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-primary/20 text-primary hover:bg-primary hover:text-white transition text-sm font-medium"
            data-testid="request-role-change-btn"
            title="Demander un changement de rôle à l'équipe DemkaBiz"
          >
            Demander un changement de rôle
          </Link>
          <Link to="/demande" className="btn-primary" data-testid="new-request-btn"><Plus className="w-4 h-4" /> Nouvelle demande</Link>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <Stat label="Demandes totales" value={requests.length} Icon={FileText} />
        <Stat label="En cours" value={requests.filter((r) => ["nouvelle", "en_traitement", "assignee"].includes(r.status)).length} Icon={Clock} />
        <Stat label="Terminées" value={requests.filter((r) => r.status === "terminee").length} Icon={CheckCircle2} />
      </div>

      <div className="card-surface overflow-hidden">
        <div className="p-5 border-b border-emerald-900/10">
          <h2 className="font-display font-semibold text-lg">Mes demandes</h2>
        </div>
        <div className="divide-y divide-emerald-900/5">
          {requests.length === 0 && <div className="p-10 text-center text-muted-foreground text-sm">Aucune demande pour le moment.</div>}
          {requests.map((r) => (
            <div key={r.id} className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`request-${r.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${STATUS_LABEL[r.status]?.cls || ""}`}>
                    {STATUS_LABEL[r.status]?.label || r.status}
                  </span>
                  <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("fr-CA")}</span>
                </div>
                <p className="font-display font-medium text-foreground">{r.category || "Demande générale"}</p>
                <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{r.description}</p>
              </div>
              <div className="text-xs text-muted-foreground">{r.city}, {r.province}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Multi-role invite: become a provider */}
      <div className="mt-8 rounded-3xl bg-primary text-primary-foreground p-6 sm:p-8 flex flex-col md:flex-row md:items-center gap-4 md:gap-8" data-testid="become-provider-cta">
        <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-accent flex items-center justify-center">
          <Sparkles className="w-7 h-7 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-display font-semibold text-xl">Vous êtes aussi prestataire, entrepreneur ou organisme ?</h3>
          <p className="text-white/85 text-sm mt-1 leading-relaxed">
            Ajoutez une fiche à votre compte pour être visible dans l'annuaire et recevoir des demandes. Vous conservez votre espace client — tout se pilote depuis le même compte.
          </p>
        </div>
        <Link
          to="/dashboard/provider"
          data-testid="become-provider-btn"
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-accent text-white hover:bg-accent/90 transition font-medium flex-shrink-0"
        >
          Créer ma fiche <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

function Stat({ label, value, Icon }) {
  return (
    <div className="card-surface p-5 flex items-center justify-between">
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="font-display font-bold text-3xl mt-1">{value}</div>
      </div>
      <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center text-primary"><Icon className="w-5 h-5" /></div>
    </div>
  );
}
